export function renderPersonaCard(persona, escapeHtml) {
  const name = escapeHtml(persona.name || "ICP")
  const headline = escapeHtml(persona.headline || "")
  const rationale = escapeHtml(persona.rationale || "")

  const titles = Array.isArray(persona.signals?.job_titles) ? persona.signals.job_titles : []
  const locations = Array.isArray(persona.signals?.geographies) ? persona.signals.geographies : []
  const industries = Array.isArray(persona.signals?.industries) ? persona.signals.industries : []
  const companyTraits = Array.isArray(persona.signals?.company_traits) ? persona.signals.company_traits : []
  const buyingSignals = Array.isArray(persona.signals?.buying_signals) ? persona.signals.buying_signals : []

  const rawPains = Array.isArray(persona.pains) ? persona.pains : []
  const pains = rawPains.map(p => typeof p === 'object' && p !== null ? (p.text || String(p)) : String(p))

  const meta = persona.metadata || {}
  const hooks = Array.isArray(meta.outbound_hooks) ? meta.outbound_hooks : []
  const objections = Array.isArray(meta.objections) ? meta.objections : []
  const channels = Array.isArray(meta.suggested_channels) ? meta.suggested_channels : []
  const confidence = meta.confidence || ""

  const renderList = (label, items, colorClass = "text-theme-muted") => {
    if (!items.length) return ""
    return `
      <div class="mt-2">
        <span class="font-semibold text-xs uppercase tracking-wide ${colorClass}">${label}</span>
        <ul class="mt-1 text-sm text-theme-primary list-disc list-inside space-y-0.5">
          ${items.map(i => `<li>${escapeHtml(i)}</li>`).join("")}
        </ul>
      </div>
    `
  }

  const renderTags = (label, items, bgClass = "bg-slate-100") => {
    if (!items.length) return ""
    return `
      <div class="mt-2">
        <span class="font-semibold text-xs uppercase tracking-wide text-theme-muted">${label}</span>
        <div class="mt-1 flex flex-wrap gap-1">
          ${items.map(i => `<span class="px-2 py-0.5 text-xs rounded-full ${bgClass} text-theme-primary">${escapeHtml(i)}</span>`).join("")}
        </div>
      </div>
    `
  }

  const personaId = persona.id || ""
  const status = persona.status || "draft"
  const statusBadge = status === "approved" 
    ? `<span class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">✓ Approved</span>`
    : status === "rejected"
    ? `<span class="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">✗ Rejected</span>`
    : `<span class="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Draft</span>`

  const borderClass = status === "approved" ? "border-green-300 bg-green-50/30" : "border-theme-secondary bg-white/50"

  return `
    <div class="border ${borderClass} rounded-xl p-5 space-y-3" data-persona-id="${personaId}">
      <div class="flex items-start justify-between gap-2">
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-theme-primary">${name}</h3>
          ${headline ? `<p class="text-sm text-theme-muted">${headline}</p>` : ""}
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          ${statusBadge}
          ${confidence ? `<span class="px-2 py-1 text-xs font-medium rounded-full ${confidence === 'high' ? 'bg-green-100 text-green-700' : confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}">${confidence}</span>` : ""}
        </div>
      </div>

      ${rationale ? `<p class="text-sm text-theme-primary border-l-2 border-k3c-coral pl-3 italic">${rationale}</p>` : ""}

      ${renderTags("Job Titles", titles, "bg-blue-50")}
      ${renderTags("Industries", industries, "bg-purple-50")}
      ${renderTags("Geographies", locations, "bg-green-50")}
      ${renderTags("Company Size", companyTraits, "bg-orange-50")}

      ${renderList("Pain Points", pains, "text-red-600")}
      ${renderList("Buying Signals", buyingSignals, "text-amber-600")}
      ${renderList("Outbound Hooks", hooks, "text-k3c-coral")}
      ${renderList("Likely Objections", objections, "text-slate-500")}

      ${channels.length ? `
        <div class="mt-3 pt-3 border-t border-theme-secondary/50">
          <span class="font-semibold text-xs uppercase tracking-wide text-theme-muted">Suggested Channels</span>
          <div class="mt-1 flex flex-wrap gap-2">
            ${channels.map(c => `<span class="px-2 py-1 text-xs font-medium rounded bg-theme-secondary/30 text-theme-primary">${escapeHtml(c)}</span>`).join("")}
          </div>
        </div>
      ` : ""}

      <div class="mt-4 pt-3 border-t border-theme-secondary/50 flex items-center gap-2">
        ${status !== "approved" ? `
          <button type="button" class="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700" 
            onclick="window.dispatchEvent(new CustomEvent('approve-persona', { detail: { id: '${personaId}' } }))">
            ✓ Approve
          </button>
        ` : `
          <button type="button" class="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300" 
            onclick="window.dispatchEvent(new CustomEvent('unapprove-persona', { detail: { id: '${personaId}' } }))">
            Revert to Draft
          </button>
        `}
        <button type="button" class="px-3 py-1.5 text-xs font-medium rounded-lg border border-theme-secondary text-theme-primary hover:bg-theme-secondary/20" 
          onclick="window.dispatchEvent(new CustomEvent('edit-persona', { detail: { id: '${personaId}' } }))">
          Edit
        </button>
      </div>
    </div>
  `
}
